import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import {
  FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import { formatExportDate } from '../../utils/exportData';
import {
  HiOutlineArrowCircleUp,
  HiOutlineRefresh,
  HiOutlineUser,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
} from 'react-icons/hi';
import { ReportGuide, periodLabel } from '../../components/SuperAdmin/ReportGuide';

const formatDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const PERIODS = [7, 30, 90, 180, 365];

const PRIORITY_LABELS = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

const TYPE_LABELS = {
  followup: 'Follow-up Escalation',
  renewal: 'Renewal Escalation',
};

const TYPE_COLORS = {
  followup: 'bg-purple-100 text-purple-700',
  renewal: 'bg-teal-100 text-teal-700',
};

const EscalationReport = () => {
  const [days, setDays] = useState(30);
  const [type, setType] = useState('');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['escalation-report', days, type],
    queryFn: () =>
      superAdminAPI.getEscalationReport({
        days,
        ...(type ? { type } : {}),
      }),
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const allRows = Array.isArray(body.rows) ? body.rows : [];
  const [filters, setFilters] = useState({ ...EMPTY_REPORT_FILTERS, assignee: '', status: '' });
  const assigneeOf = (r) => r.assignedEmployeeName || 'Unassigned';
  const rows = filterReportRows(allRows, filters, {
    searchOf: (r) => [r.organizationName, r.entityLabel, r.escalationReason, r.assignedEmployeeName],
  }).filter((r) =>
    (!filters.assignee || assigneeOf(r) === filters.assignee)
    && (!filters.status || (filters.status === 'resolved' ? r.status === 'resolved' : r.status !== 'resolved')));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineArrowCircleUp className="w-7 h-7 text-primary-500" />
            Escalations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Work that was overdue and got raised to a manager
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-auto px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          >
            <option value="">All types</option>
            <option value="followup">Follow-up Escalations</option>
            <option value="renewal">Renewal Escalations</option>
          </select>
          <span className="text-xs font-medium text-gray-500">Show last</span>
          <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === p ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="escalations"
        summary="When a follow-up or renewal is not handled in time, it is escalated (raised) to a manager. This lists those cases."
        points={[
          ["Follow-up escalations", "Lead follow-ups that went overdue."],
          ["Renewal escalations", "Subscription renewals that went overdue."],
          ["Still open", "Escalated and still waiting for action."],
          ["Resolved", "Handled after being escalated."],
        ]}
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Escalations</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalEscalations ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Last {body.windowDays ?? days} days</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Follow-up Escalations</p>
          <p className="text-3xl font-bold text-purple-500 mt-2">{stats.followUpEscalations ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Overdue follow-ups raised to managers</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Renewal Escalations</p>
          <p className="text-3xl font-bold text-teal-500 mt-2">{stats.renewalEscalations ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Overdue subscription renewals raised</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Still Open</p>
          <p className={`text-3xl font-bold mt-2 ${(stats.open ?? 0) === 0 ? 'text-green-500' : 'text-red-500'}`}>
            {stats.open ?? 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">Awaiting action after escalation</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Resolved</p>
          <p className="text-3xl font-bold text-green-500 mt-2">{stats.resolved ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Completed after escalation</p>
        </div>
      </div>

      {/* Escalations table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Escalated Items</h2>
          <span className="text-xs text-gray-400">
            {rows.length === allRows.length ? `${rows.length} shown` : `${rows.length} of ${allRows.length} shown`}
          </span>
        </div>

        {allRows.length > 0 && (
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            searchPlaceholder="Search organization, item, reason, employee…"
            organizations={uniqueSorted(allRows.map((r) => r.organizationName))}
            extraActive={!!(filters.assignee || filters.status)}
            onResetExtra={() => setFilters((f) => ({ ...f, assignee: '', status: '' }))}
            exportProps={{
              fileName: `escalations-${days}d${type ? `-${type}` : ''}`,
              getRows: () => rows,
              columns: [
                { header: 'Type', value: (r) => TYPE_LABELS[r.escalationType] || r.escalationType },
                { header: 'Organization', value: (r) => r.organizationName },
                { header: 'Item', value: (r) => r.entityLabel },
                { header: 'Escalation Reason', value: (r) => r.escalationReason },
                { header: 'Priority', value: (r) => PRIORITY_LABELS[r.priority] || r.priority },
                { header: 'Assigned Employee', value: assigneeOf },
                { header: 'Employee Role', value: (r) => r.assignedEmployeeRole },
                { header: 'Escalated On', value: (r) => formatExportDate(r.escalationDate) },
                { header: 'Status', value: (r) => (r.status === 'resolved' ? 'Resolved' : 'Open') },
              ],
            }}
          >
            <FilterSelect
              value={filters.assignee}
              onChange={(v) => setFilters((f) => ({ ...f, assignee: v }))}
              allLabel="All Employees"
              options={uniqueSorted(allRows.map(assigneeOf)).map((a) => [a, a])}
            />
            <FilterSelect
              value={filters.status}
              onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              allLabel="All Status"
              options={[['open', 'Open'], ['resolved', 'Resolved']]}
            />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading escalation report…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allRows.length > 0 ? 'No escalations match these filters' : 'No escalations in this period'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Escalation Reason</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Assigned Employee</th>
                  <th className="px-4 py-3">Escalated On</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={`${row.escalationType}-${row.escalationId}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${TYPE_COLORS[row.escalationType] || 'bg-gray-100 text-gray-600'}`}>
                        {TYPE_LABELS[row.escalationType] || row.escalationType}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{row.organizationName || '—'}</p>
                        <p className="text-xs text-gray-400">{row.entityLabel || '—'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-gray-600 max-w-[260px]">{row.escalationReason}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[row.priority] || 'bg-gray-100 text-gray-600'}`}>
                        {PRIORITY_LABELS[row.priority] || row.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.assignedEmployeeName ? (
                        <span className="inline-flex items-center gap-1.5 text-gray-600 text-xs">
                          <HiOutlineUser className="w-3.5 h-3.5 text-gray-400" />
                          <span>
                            {row.assignedEmployeeName}
                            {row.assignedEmployeeRole && (
                              <span className="text-gray-400"> · {row.assignedEmployeeRole}</span>
                            )}
                          </span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{formatDateTime(row.escalationDate)}</td>
                    <td className="px-4 py-3">
                      {row.status === 'resolved' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          <HiOutlineCheckCircle className="w-3.5 h-3.5" />
                          Resolved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          <HiOutlineExclamationCircle className="w-3.5 h-3.5" />
                          Open
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EscalationReport;